import { Avatar, Card, Col, Row, Tag } from "antd";
import Moment from "react-moment";
import PropTypes from "prop-types";
import { useUserContext } from "../../contexts/UserContext";
import { NavLink } from "react-router-dom";
import { TacheService } from "../../services/TacheService";
import { useEffect, useState } from "react";
import { StatutService } from "../../services/StatutService";
import { catchError } from "../../services/DaoService";
import Toast from "../../helpers/Toast";
import { InlineEditableInput } from "../utils/InlineEditableInput";
import { PersonnelService } from "../../services/PersonnelService";
import UserService from "../../services/UserService";
import moment from "moment-timezone";
import { tachesState } from "../../state/actions/tachesState";
import { isTacheLocked } from "../../helpers/permissions";
import styles from "./TacheDetails.module.scss";

const TacheDetails = ({ tache }) => {
  const { check, currentUser } = useUserContext();
  const [statuts, setStatuts] = useState([]);
  const [personnels, setPersonnels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const canManageTache = currentUser?.id === tache?.user_id ||
    tache?.responsable_id === currentUser?.personnel?.id ||
    currentUser?.id === tache?.affaire?.user_id ||
    currentUser?.id === tache?.affaire?.responsable_id;
  // Une tâche résolue, ou rattachée à un dossier clôturé, ne peut plus être
  // modifiée en inline (le Path reste la seule exception pour rouvrir).
  const canEdit = canManageTache && !isTacheLocked(tache);
  const canEditOwner = canEdit;

  useEffect(() => {
    Promise.all([
      StatutService.getInstance().all(),
      PersonnelService.getInstance().all(),
      UserService.getInstance().all(),
    ]).then(([statuts, personnels, users]) => {
      setStatuts(statuts.data);
      setPersonnels(personnels.data);
      setUsers(users.data);
    }).catch(catchError);
  }, [tache.uid]);

  const handleFieldChange = (value, fieldName, record) => {
    setLoading(true);
    TacheService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then((response) => {
        Toast.success(response.message);
        tachesState.updateTacheInList(response.data);
      })
      .catch((error) => {
        catchError(error);
      }).finally(() => setLoading(false));
  };

  return (
    <Card>
      <Row gutter={[16, 12]}>
        <Col xs={24}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Nom</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput type="text" canEdit={canEdit} fieldName="nom" value={tache?.nom} record={tache} onChange={handleFieldChange}>
                {tache?.nom ?? '—'}
              </InlineEditableInput>
            </div>
          </div>
        </Col>
        {tache?.affaire && (
          <Col xs={24}>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>Dossier</div>
              <div className={styles.fieldValue}>
                {check('VIEW-AFFAIRES') ? (
                  <NavLink to={`/affaire/${tache.affaire.uid}`}>{tache.affaire.nom}</NavLink>
                ) : tache.affaire.nom}
              </div>
            </div>
          </Col>
        )}
        <Col xs={24} sm={12}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Date limite</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput type="date" canEdit={canEdit} fieldName="date_limite" value={moment(tache?.date_limite).format('YYYY-MM-DD')} record={tache} onChange={handleFieldChange}>
                {tache?.date_limite ? (
                  <Tag color={tache.retard ? 'red' : 'default'}>
                    <Moment format="DD/MM/YYYY">{tache?.date_limite}</Moment>
                  </Tag>
                ) : '—'}
              </InlineEditableInput>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Priorité</div>
            <div className={styles.fieldValue}>
              <Tag color={tache?.priorite?.couleur} style={{ fontWeight: 500 }}>{tache?.priorite?.nom ?? '—'}</Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Statut</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput type="select"
                canEdit={canEdit}
                fieldName="statut_id" value={tache?.statut_id} record={tache}
                onChange={handleFieldChange} options={statuts.map(statut => ({
                  value: statut.id,
                  label: statut.nom,
                  children: statut.nom
                }))}>
                <Tag color={tache?.statut?.couleur} style={{ fontWeight: 500 }}>{tache?.statut?.nom ?? '—'}</Tag>
              </InlineEditableInput>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Responsable</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput loading={loading} type="select" fieldName="responsable_id" record={tache}
                onChange={handleFieldChange} placeholder="Sélectionner un personnel"
                value={tache?.responsable_id} key={"responsable_id"} options={personnels.map(personnel => ({
                  value: personnel.id,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar size={24} src={personnel.avatar_link} alt={personnel.name} />
                      <span>{personnel.name}</span>
                    </div>
                  ),
                  children: personnel.name
                }))}
                canEdit={canEdit}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar size={24} src={tache?.responsable?.avatar_link} alt={tache?.responsable?.name} />
                  {tache?.responsable?.name ?? '—'}
                </div>
              </InlineEditableInput>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Propriétaire</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput
                canEdit={canEditOwner}
                fieldName="user_id"
                type="select"
                placeholder="Sélectionner un utilisateur"
                value={tache?.user_id}
                record={tache}
                onChange={handleFieldChange}
                optionFilterProp="full_name"
                options={users.map(user => ({
                  value: user.id,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar size={24} src={user.avatar} alt={user.name} />
                      <span>{user.name}</span>
                    </div>
                  ),
                  full_name: user.name,
                }))}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar size={24} src={tache?.user?.avatar} alt={tache?.user?.name} />
                  {tache?.user?.name ?? '—'}
                </div>
              </InlineEditableInput>
            </div>
          </div>
        </Col>
        <Col xs={24}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Description</div>
            <div className={styles.fieldValue}>
              <InlineEditableInput type="textarea" canEdit={canEdit} fieldName="description" value={tache?.description} record={tache} onChange={handleFieldChange}>
                <div className={styles.description}>{tache?.description ?? '—'}</div>
              </InlineEditableInput>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

TacheDetails.propTypes = {
  tache: PropTypes.object,
};

export default TacheDetails;
